import { FormControl, MenuItem, SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useQuotation from '../../../hooks/quotations/useQuotation';
import {
    QuotationAccordionProps,
    QuotationUserProps,
} from '../../../models/quotations/Quotation';
import { CountryNameDisplay } from '../../shared/CountryNameDisplay';
import SelectStyled from '../../shared/SelectStyled';
import TextFieldStyled from '../../shared/TextFieldStyled';
import { QuotationFormItemWrapper } from '../shared/QuotationFormItemWrapper';
import { QuotationFormRowWrapper } from '../shared/QuotationFormRowWrapper';
import { QuotationNextButton } from '../shared/QuotationNextButton';

export const QuotationSellerDetails: React.FC<QuotationAccordionProps> = (
    props
) => {
    const { handleNext, index, quoteData, updateProducts } = props;
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_FROM',
    });
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const quotation = useQuotation();
    const {
        register,
        handleSubmit,
        formState: { dirtyFields },
    } = useForm<QuotationUserProps>({
        defaultValues: quotation
            ? quotation.setUserDefaultValues(quoteData.seller)
            : null,
    });
    const [country, setCountry] = useState('HU');

    const onSubmit: SubmitHandler<QuotationUserProps> = (data) => {
        const fieldsToSend = quotation.getModifiedFields(dirtyFields, data);
        if (Object.keys(fieldsToSend).length > 0) {
            quotation.saveSeller(fieldsToSend, quoteData.id).then((resp) => {
                updateProducts();
                handleNext(index, resp);
            });
        } else {
            handleNext(index);
        }
    };

    const handleCountryChange = (event: SelectChangeEvent) => {
        setCountry(event.target.value as string);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('COMPANY_NAME')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('company')}
                                disabled
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('TAX_NUMBER')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('tax_number')}
                                disabled
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('PHONE')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('phone')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth variant="filled">
                        <DemoItem label={t('COUNTRY')}>
                            <SelectStyled
                                {...register('country', {
                                    required: t(
                                        'FORM_VALIDATION_ERROR.REQUIRED'
                                    ).toString(),
                                    onChange: (e) => handleCountryChange(e),
                                })}
                                value={country}
                                sx={{
                                    '& .MuiFilledInput-input': {
                                        padding:
                                            '10px 10px 11px 10px !important',
                                    },
                                }}
                            >
                                <MenuItem value={'HU'}>
                                    <CountryNameDisplay countryCode={'HU'} />
                                </MenuItem>
                            </SelectStyled>
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('POSTCODE')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('postcode')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('CITY')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('city')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationFormRowWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('ADDRESS1')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('address1')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
                <QuotationFormItemWrapper>
                    <FormControl fullWidth>
                        <DemoItem label={t('ADDRESS2')}>
                            <TextFieldStyled
                                sx={{ backgroundColor: 'white' }}
                                {...register('address2')}
                            />
                        </DemoItem>
                    </FormControl>
                </QuotationFormItemWrapper>
            </QuotationFormRowWrapper>
            <QuotationNextButton fullWidth={small} />
        </form>
    );
};
